/**
 * The one reading of what Leo spotted on a question.
 *
 * **The bug this exists to kill.** CASE-2026-002 read *Needs fixing* in the
 * bank table, *Looks good* in the composer, and showed no card at all in the
 * preview sheet — the same question, at the same moment, described three
 * ways. Each surface had grown its own path to the answer:
 *
 * - the table called `spottedForLibraryItem`
 * - the sheet called its own local `spottedForPreview`
 * - the composer called `composeLeoSpotted`
 * - the paper roster read `questionCatalogById(...).aiSpotted` raw
 *
 * Four functions, four sets of inputs, four chances to forget the session
 * overlay or the catalog baseline. Any one of them drifting produces exactly
 * what the author saw.
 *
 * **The rule now: no surface computes a tag.** Every surface converts its
 * row into a `LeoSpottedSubject` and calls `readLeoSpotted`. There is one
 * engine, it applies the overlay itself, and it returns the tag and the
 * findings together so a badge can never disagree with the panel under it —
 * they are two renderings of one object.
 *
 * The adapters at the bottom are the only thing a surface should need.
 */

import {
  aiSpottedBrief,
  caseIssueIdsClearedByEdit,
  choiceSetIsBroken,
  libraryCaseTag,
  withoutLeoSpottedTitleFindings,
  type LibraryAiSpotted,
} from "@/lib/library-ai-spotted"
import { overlayForLibraryBankRow } from "@/lib/library-bank-row-overlays"
import { catalogSpottedKey } from "@/lib/library-ai-spotted"
import { questionCatalogById } from "@/lib/mock/question-catalog"
import type { LibraryItem } from "@/lib/mock/library"
import {
  normalizeQuestionType,
  questionTypeAcceptsChoices,
} from "@/lib/question-type"
import { choicesNeedStemHarmonize } from "./stem-choice-fit"
import {
  LEO_SPOTTED_PBI_FLOOR,
  leoSpottedDimension,
  leoSpottedTag,
  type LeoSpottedIssueInput,
} from "./leo-spotted-queue"

/** The finding a stem rewrite creates when it orphans the choices. */
export const CHOICES_FIT_ID = "choices-fit"

/** The finding a point-biserial below the review floor raises. */
export const PERFORMANCE_ID = "performance-pbi"

/** Every issue id that represents separation / PBI review for this question. */
export function performanceFindingIds(
  issues: readonly { id: string; label: string }[],
): string[] {
  const fromIssues = issues
    .filter(
      (issue) =>
        issue.id === PERFORMANCE_ID ||
        issue.id === "pbi-review" ||
        /\bPBI\b|not separating|discriminat|below the review floor/i.test(
          `${issue.id} ${issue.label}`,
        ),
    )
    .map((issue) => issue.id)
  return [...new Set([...fromIssues, PERFORMANCE_ID, "pbi-review"])]
}

/** A question, in the only shape the engine reads. */
export type LeoSpottedSubject = {
  /** Row id. The overlay and the catalog are both keyed off this. */
  id: string
  /** Public code, e.g. CASE-2026-002. A second key into the overlay. */
  questionId?: string
  stem: string
  title?: string
  type?: string
  options?: readonly { text?: string; isCorrect?: boolean }[]
  rationale?: string
  bloom?: string
  difficulty?: string
  categoryIds?: readonly string[] | null
  categoryUnassigned?: boolean
  /** `null` means never sat, which is not the same as scoring badly. */
  pbi?: number | null
  timesUsed?: number
  /**
   * How the question sits against the blueprint of the section it is being
   * looked at in. Absent where there is no section (the bank on its own).
   * A misalignment is a finding like any other, so the badge, the table
   * popover, the sheet and the editor all carry it, instead of the sheet
   * alone listing a problem the table calls "Not checked".
   */
  alignment?: { aligned: boolean; detail: string } | null
}

/** The finding a blueprint miss raises. */
export const BLUEPRINT_ALIGNMENT_ID = "assessment-blueprint-alignment"

export type LeoSpottedReading = {
  /** The badge. `null` is the muted dash: clean, or never audited. */
  tag: LibraryAiSpotted | null
  /** The findings behind the badge. Never disagrees with it. */
  issues: LeoSpottedIssueInput[]
  /** One sentence for a tooltip. */
  why: string
  /** True when the author has edited this question in this session. */
  edited: boolean
  /** Findings the author has answered. Still shown, as resolved cards. */
  resolvedIssues: LeoSpottedIssueInput[]
  /**
   * How many findings this question had that the author has since answered.
   *
   * Without this the All good state is unreachable: resolved findings drop
   * out of `issues`, so the queue rebuilds empty and the panel renders
   * nothing — the author fixes the last thing and the card silently
   * disappears instead of telling them they are done. A count separates
   * "clean, nothing to say" from "you just cleared this".
   */
  resolvedCount: number
}

/**
 * The reading.
 *
 * Order matters: the session overlay is applied first so an edit made in the
 * composer is what gets audited, then the result is compared against the
 * catalog baseline so findings the edit actually cleared drop out. Skipping
 * either step is how a surface ends up reporting a defect the author fixed
 * ten seconds ago.
 */
/**
 * Findings the question itself can answer for.
 *
 * Each returns true while the defect is still present. A recorded
 * resolution is ignored for these: the author pressing a button does
 * not put a rationale on the question, and a card that says Resolved
 * over an empty field is the panel lying about the work.
 */
const STILL_TRUE: Record<
  string,
  (subject: LeoSpottedSubject, live: {
    rationale?: string
    bloom?: string
    categoryIds?: readonly string[] | null
    categoryUnassigned?: boolean
  }) => boolean
> = {
  rationale: (_subject, live) => !live.rationale?.trim(),
  "classification-bloom": (_subject, live) => !live.bloom?.trim(),
  "classification-category": (_subject, live) =>
    live.categoryUnassigned === true ||
    !live.categoryIds?.some((id) => id.trim()),
}

export function readLeoSpotted(subject: LeoSpottedSubject): LeoSpottedReading {
  const overlay =
    overlayForLibraryBankRow(subject.id, subject.questionId) ?? undefined
  const catalog =
    questionCatalogById(subject.id) ?? questionCatalogById(subject.questionId)

  const stem = overlay?.stem ?? subject.stem
  const options = overlay?.options ?? subject.options
  const categoryIds = overlay?.categoryIds?.length
    ? overlay.categoryIds
    : subject.categoryIds
  const categoryUnassigned = overlay?.categoryIds?.length
    ? false
    : (overlay?.categoryUnassigned ?? subject.categoryUnassigned)
  const bloom = overlay?.bloomLevel ?? subject.bloom
  const difficulty = overlay?.difficulty ?? subject.difficulty
  const edited = Boolean(overlay)

  /**
   * Placeholder choices outrank everything, which means they lead — not
   * that they are the only thing reported. This used to return early
   * with one finding and discard the rest, so the same question read as
   * three findings in the preview sheet (a bank row, no choices to
   * check) and one in the composer (live form state, choices present).
   *
   * Read off the choices on every read, so it clears when they do; see
   * `selfClearing`.
   */
  const placeholderChoices: LeoSpottedIssueInput | null = choiceSetIsBroken(options)
    ? {
        id: "choices",
        label: "Answer choices include All of the above and None of the above",
        cover: "choices",
        outcome: "Replace placeholders",
        selfClearing: true,
        instruction:
          "Replace All of the above and None of the above with real distractors. Keep every retained choice in its current letter.",
      }
    : null

  const spottedKey =
    catalogSpottedKey(subject.id) ||
    catalogSpottedKey(subject.questionId) ||
    subject.id
  const caseTag =
    libraryCaseTag(spottedKey) ??
    libraryCaseTag(subject.id) ??
    libraryCaseTag(subject.questionId)
  /**
   * What the author has answered.
   *
   * Recorded first, inferred second. `caseIssueIdsClearedByEdit` guesses
   * from the text — it asks whether the rewrite still shares three long
   * words with the original — and a good rewrite often does not, which is
   * why findings kept coming back after they were fixed. The recorded set
   * is the author's own action and is not second-guessed; the heuristic
   * stays only to catch edits made by hand, where no finding was targeted
   * and so nothing was recorded.
   */
  const resolved = new Set([
    ...(overlay?.resolvedIssueIds ?? []),
    ...caseIssueIdsClearedByEdit({
      id: spottedKey,
      stem,
      baseStem: catalog?.stem,
      choiceTexts: options?.map((option) => option.text ?? ""),
      baseChoiceTexts: catalog?.options?.map((option) => option.text),
    }),
  ])

  /**
   * The question overrules the record.
   *
   * A finding whose defect the reading can still see stays open however
   * it was marked, so the card cannot say Resolved over an empty
   * rationale or an unset Bloom level.
   */
  for (const id of [...resolved]) {
    const stillTrue = STILL_TRUE[id]
    if (
      stillTrue?.(subject, {
        rationale: overlay?.rationale ?? subject.rationale,
        bloom,
        categoryIds,
        categoryUnassigned,
      })
    ) {
      resolved.delete(id)
    }
  }

  const brief = caseTag
    ? aiSpottedBrief({
        id: spottedKey,
        tag: caseTag,
        title: subject.title,
        pbi: subject.pbi,
        timesUsed: subject.timesUsed,
      })
    : null

  const allFindings = withoutLeoSpottedTitleFindings(brief?.issues ?? [])
  const resolvedCount = allFindings.filter((issue) => resolved.has(issue.id)).length
  const toInput = (issue: { id: string; label: string }): LeoSpottedIssueInput => ({
    id: issue.id,
    label: issue.label,
    cover: coverForIssueId(issue.id),
  })
  const issues: LeoSpottedIssueInput[] = allFindings
    .filter((issue) => !resolved.has(issue.id))
    .map(toInput)
  /**
   * Answered, and still worth a card.
   *
   * Kept apart from `issues` so everything that counts open findings or
   * derives the row's tag reads the same as before; the queue is the only
   * thing that wants these, and it wants them so a fixed card stays in
   * the stack with its Reopen rather than disappearing.
   */
  const resolvedIssues: LeoSpottedIssueInput[] = allFindings
    .filter((issue) => resolved.has(issue.id))
    .map(toInput)

  // Front of the queue, and never twice: an authored choices finding on
  // the same question already says this.
  if (placeholderChoices && !issues.some((issue) => issue.id === placeholderChoices.id)) {
    issues.unshift(placeholderChoices)
  }

  /**
   * A stem rewrite that orphaned the choices.
   *
   * Turning "Beta oxidation occurs in:" into "In which compartment does
   * beta oxidation occur?" leaves options that still read as endings for
   * the sentence that is gone ("in the mitochondrial matrix"). The author
   * fixed one thing and silently broke another, and nothing told them.
   * `choicesNeedStemHarmonize` has known how to spot this all along; it
   * just had no way onto the panel. Now it is a finding like any other, so
   * it arrives as its own card with its own targeted fix the moment the
   * new stem lands.
   */
  if (
    questionTypeAcceptsChoices(subject.type) &&
    !resolved.has(CHOICES_FIT_ID) &&
    !issues.some((issue) => issue.id === CHOICES_FIT_ID) &&
    choicesNeedStemHarmonize(
      stem,
      (options ?? []).map((option, index) => ({
        id: String(index),
        text: option.text ?? "",
      })),
    )
  ) {
    issues.push({
      id: CHOICES_FIT_ID,
      label: "The choices still read as endings for the old stem",
      cover: "choices",
      selfClearing: true,
      outcome: "Harmonize choices",
      instruction:
        "Harmonize the answer choices to the new stem so each one reads as a standalone answer. Keep every choice in its current letter and change nothing else.",
    })
  }

  /**
   * A PBI below the review floor, as a finding.
   *
   * The badge already went red for this (`leoSpottedTag` reads the
   * number), but nothing produced a card, so a question flagged Needs
   * fixing for weak discrimination opened on a panel that talked about
   * its Bloom's level instead. The PRD calls this the Performance Alert
   * card and defers its remediation, which is why the CTA offers a
   * diagnosis rather than a rewrite.
   */
  if (
    subject.pbi != null &&
    subject.pbi < LEO_SPOTTED_PBI_FLOOR &&
    !resolved.has(PERFORMANCE_ID) &&
    !issues.some((issue) => leoSpottedDimension(issue) === "performance")
  ) {
    issues.push({
      id: PERFORMANCE_ID,
      label: `PBI ${subject.pbi.toFixed(2)} is below the review floor of 0.15`,
      cover: "difficulty",
      instruction:
        "Look at how the cohort answered this question and say why it is not separating strong from weak examinees. Propose a fix I can review before applying.",
    })
  }

  if (subject.alignment && !subject.alignment.aligned) {
    issues.unshift({
      id: BLUEPRINT_ALIGNMENT_ID,
      label: subject.alignment.detail,
      cover: "alignment",
    })
  }

  // Classification gaps are findings, but never badge colour. See
  // `leoSpottedTag` and the PRD's column separation rule.
  if (categoryUnassigned || (categoryIds != null && !categoryIds.some((id) => id.trim()))) {
    if (!issues.some((issue) => issue.cover === "category")) {
      issues.push({
        id: "classification-category",
        label: "Category is not mapped",
        cover: "category",
      })
    }
  }
  if (!bloom?.trim() && !issues.some((issue) => issue.id === "classification-bloom")) {
    issues.push({
      id: "classification-bloom",
      label: "Bloom's level is not set",
      cover: "bloom",
    })
  }

  const contentIssues = issues.filter(
    (issue) => issue.cover !== "category" && issue.cover !== "bloom",
  )
  const onlyExposure =
    contentIssues.length > 0 &&
    contentIssues.every((issue) => leoSpottedDimension(issue) === "overuse")

  /**
   * The authored verdict wins where there is one.
   *
   * Deriving the tag from covers alone flattens intent the case table
   * already encodes: q2's finding is "Used 3 times, returning students may
   * recognise the wording", which wears the `stem` cover but is an exposure
   * warning, not a defect. Reading the cover turned every Worth a look in
   * the bank into Needs fixing. So: case tag when the tables know this
   * question, derived tag for the ~1,200 rows they never covered.
   */
  const tag: LibraryAiSpotted | null = caseTag
    ? onlyExposure
      ? "worth-a-look"
      : contentIssues.length === 0
      ? caseTag === "weak-performance"
        ? "weak-performance"
        : null
      : caseTag
    : leoSpottedTag({
        issues,
        pbi: subject.pbi ?? null,
        timesUsed: subject.timesUsed,
        bloom,
        difficulty,
        categoryIds,
        categoryUnassigned,
      })

  // A question that misses the blueprint is not fine for this paper, whatever
  // else Leo thinks of it. Weak performance keeps its own, stronger badge.
  const misaligned = Boolean(subject.alignment && !subject.alignment.aligned)
  const finalTag: LibraryAiSpotted | null =
    misaligned && tag !== "weak-performance" && tag !== "needs-fixing"
      ? "needs-fixing"
      : tag

  return {
    // Placeholders are a defect on their own, so they set the badge even
    // when the derived tag would have been softer.
    tag: placeholderChoices ? "needs-fixing" : finalTag,
    edited,
    issues,
    resolvedCount,
    resolvedIssues,
    why: placeholderChoices
      ? "The answer choices are still placeholders."
      : contentIssues.length === 0
        ? edited
          ? "Leo has nothing else to flag on this wording."
          : "Nothing else needs a change."
        : contentIssues.map((issue) => issue.label).join(" "),
  }
}

/** Issue ids predate covers, so the cover is read back out of the id. */
function coverForIssueId(id: string): string {
  if (id.startsWith("classification-")) {
    return id === "classification-category" ? "category" : "bloom"
  }
  if (id.startsWith("choices")) return "choices"
  if (id.startsWith("performance") || id === "pbi-review") return "difficulty"
  return id
}

// ─── Adapters ───────────────────────────────────────────────────────────────
// The only entry points a surface should reach for.

export function leoSpottedSubjectFromLibraryItem(
  item: LibraryItem,
): LeoSpottedSubject {
  return {
    id: item.id,
    questionId: item.questionId,
    title: item.title,
    stem: item.stem,
    type: item.type,
    options: item.options,
    rationale: typeof item.rationale === "string" ? item.rationale : undefined,
    bloom: typeof item.bloomLevel === "string" ? item.bloomLevel : undefined,
    difficulty: item.difficulty || undefined,
    categoryIds: item.categoryIds,
    categoryUnassigned: item.categoryUnassigned,
    pbi: item.pbi ?? item.ptBiserial ?? null,
    timesUsed: item.usageCount ?? item.examUsageCount,
  }
}

/** A pool or paper row. `pbi` arrives as `"--"` when never sat. */
export function leoSpottedSubjectFromBankRow(row: {
  id: string
  questionId?: string
  title?: string
  stem: string
  type?: string
  pbi?: string | number
  bloom?: string
  blooms?: string
  difficulty?: string
  categoryIds?: readonly string[]
  timesUsed?: number
  usedCount?: number
  carriedChoices?: readonly { text?: string; isCorrect?: boolean }[]
}, extra?: { alignment?: LeoSpottedSubject["alignment"] }): LeoSpottedSubject {
  const pbi = Number(row.pbi)
  const catalog = questionCatalogById(row.id) ?? questionCatalogById(row.questionId)
  return {
    id: row.id,
    questionId: catalog?.questionId ?? row.questionId ?? row.id,
    title: row.title,
    stem: row.stem,
    type: row.type ? (normalizeQuestionType(row.type) ?? row.type) : undefined,
    options: row.carriedChoices,
    bloom: row.bloom ?? row.blooms,
    difficulty: row.difficulty,
    categoryIds: row.categoryIds,
    pbi: Number.isFinite(pbi) ? pbi : null,
    timesUsed: row.timesUsed ?? row.usedCount,
    alignment: extra?.alignment ?? null,
  }
}

/** The composer's live form state. */
export function leoSpottedSubjectFromComposer(input: {
  questionId?: string
  title?: string
  stem: string
  type?: string
  options?: readonly { text?: string; isCorrect?: boolean }[]
  rationale?: string
  bloom?: string
  difficulty?: string
  categoryIds?: readonly string[]
  pbi?: number | null
  timesUsed?: number
  alignment?: LeoSpottedSubject["alignment"]
}): LeoSpottedSubject {
  return {
    id: input.questionId ?? "composer-draft",
    alignment: input.alignment ?? null,
    questionId: input.questionId,
    title: input.title,
    stem: input.stem,
    type: input.type,
    options: input.options,
    rationale: input.rationale,
    bloom: input.bloom,
    difficulty: input.difficulty,
    categoryIds: input.categoryIds,
    pbi: input.pbi ?? null,
    timesUsed: input.timesUsed,
  }
}
